import { memo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { Search, LogOut, Menu, MoonStar, SunMedium, Plus, Brain } from 'lucide-react';
import FocusTimer from './FocusTimer';
import GlobalSelectionIndicator from './GlobalSelectionIndicator';
import NudgeBell from './NudgeBell';
import { DbLatencyPill } from './topbar/DbLatencyPill';
import { BrandMark } from './BrandMark';

const BRAIN_DUMP_LONG_PRESS_MS = 450;


function Topbar({ title, onCommandBarOpen, onSearchOpen, onQuickAddOpen, onBrainDumpOpen, onMobileMenuToggle }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { preferences } = usePreferences();
  const [showMenu, setShowMenu] = useState(false);
  // Long-press refs for the topbar `+` button: holding it for ~450ms
  // opens QuickAdd in Brain Dump mode (rapid-fire chained capture).
  // Mobile users get this via touch; desktop via mouse-down hold.
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  const startQuickAddLongPress = () => {
    longPressFiredRef.current = false;
    longPressTimerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      onBrainDumpOpen?.();
    }, BRAIN_DUMP_LONG_PRESS_MS);
  };
  const cancelQuickAddLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const handleQuickAddClick = () => {
    // If long-press fired, suppress the synthetic click that follows
    // pointerup. Otherwise this is a normal tap → open QuickAdd.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onQuickAddOpen?.();
  };
  const initial = user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U';

  return (
    <header data-testid="topbar" className="pd-topbar" role="banner">
      <div className="pd-topbar-left">
        <button data-testid="mobile-menu-button" className="pd-icon-button pd-mobile-only" onClick={onMobileMenuToggle} aria-label="Open navigation menu">
          <Menu className="w-5 h-5" />
        </button>
        {/* Mobile-only brand mark — the yellow `Q` tile that matches
            the PWA home-screen icon. Replaces the wordmark+divider
            which would crowd the small viewport. */}
        <BrandMark
          variant="mark"
          size="sm"
          testId="topbar-brand-mark"
          className="pd-mobile-only pd-topbar-brand-mark"
        />
        <BrandMark
          variant="wordmark"
          size="sm"
          testId="topbar-brand"
          className="pd-topbar-brand-wordmark"
        />
        <span aria-hidden="true" className="pd-topbar-brand-divider" />
        <h1 className="text-[14px] font-semibold truncate" style={{ letterSpacing: '-0.02em', color: 'var(--pd-text)' }}>{title}</h1>
      </div>

      <div className="pd-topbar-right">
        <GlobalSelectionIndicator />
        <DbLatencyPill />
        {preferences.commandBarEnabled ? (
          <button data-testid="topbar-commandbar-trigger" onClick={onCommandBarOpen} className="pd-icon-button pd-btn-press" title="Command bar (⌘K)" aria-label="Open command bar (Cmd+K)">
            <Search className="w-[16px] h-[16px]" />
          </button>
        ) : null}

        <button data-testid="topbar-search-button" onClick={onSearchOpen} className="pd-icon-button pd-btn-press" title="Global search (⌘/)" aria-label="Open global search (Cmd+/)">
          <span className="kbd-pill text-[10px]">⌘/</span>
        </button>

        {/* Universal Quick Add — 3-click capture for any entity type.
            Tap → opens normal Quick Add. Long-press (≥450ms) → opens
            in Brain Dump mode for rapid-fire chained capture. */}
        <button
          data-testid="topbar-quickadd-button"
          onClick={handleQuickAddClick}
          onPointerDown={startQuickAddLongPress}
          onPointerUp={cancelQuickAddLongPress}
          onPointerLeave={cancelQuickAddLongPress}
          onPointerCancel={cancelQuickAddLongPress}
          className="pd-icon-button pd-btn-press"
          title="Quick add (⌘N) · hold for Brain dump (⇧⌘D)"
          aria-label="Open quick add (Cmd+N), hold for brain dump"
          style={{ color: 'var(--pd-accent)' }}
        >
          <Plus className="w-[18px] h-[18px]" />
        </button>

        {/* Explicit Brain Dump entry point — long-press on `+` works,
            but most users won't discover it. A dedicated button gives
            single-click access to the rapid-fire chained-capture mode.
            Visible on every viewport including mobile (480px and up).
            Uses the brand amber so the button reads as the warm,
            creative-flow affordance it is (instead of the monochrome
            `--pd-accent` which renders as cool grey-white). */}
        <button
          data-testid="topbar-braindump-button"
          onClick={() => onBrainDumpOpen?.()}
          className="pd-icon-button pd-btn-press"
          title="Brain dump — chained rapid-fire capture (⇧⌘D)"
          aria-label="Open brain dump (Cmd+Shift+D)"
          style={{ color: 'var(--pd-brand)' }}
        >
          <Brain className="w-[18px] h-[18px]" />
        </button>

        <FocusTimer />

        <NudgeBell />

        <button data-testid="theme-toggle-button" onClick={toggleTheme} className="pd-icon-button pd-btn-press" title={`Switch to ${theme === 'night' ? 'light' : 'dark'} theme`} aria-label={`Switch to ${theme === 'night' ? 'light' : 'dark'} theme`}>
          {theme === 'night' ? <SunMedium className="w-4 h-4" /> : <MoonStar className="w-4 h-4" />}
        </button>

        <div className="relative">
          <button data-testid="topbar-avatar" onClick={() => setShowMenu((prev) => !prev)} className="pd-avatar-button" aria-label="Open user menu" aria-haspopup="menu" aria-expanded={showMenu}>{initial}</button>
          {showMenu ? (
            <>
              <div className="fixed inset-0 z-[200]" onClick={() => setShowMenu(false)} />
              <div role="menu" className="absolute right-0 top-12 w-56 rounded-2xl py-2 z-[201] animate-fade-in" style={{ background: 'var(--pd-popover)', border: '1px solid var(--pd-popover-border)', boxShadow: 'var(--pd-shadow-3)' }}>
                <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--pd-border)' }}>
                  <div className="text-[12px] font-semibold" style={{ color: 'var(--pd-text)' }}>{user?.name}</div>
                  <div className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--pd-subtle)' }}>{user?.email}</div>
                </div>
                <button data-testid="logout-button" onClick={logout} role="menuitem" className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[12px] hover:bg-black/[0.03] transition-colors" style={{ color: 'var(--pd-crimson)' }}>
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}

/**
 * `React.memo` so the topbar doesn't re-render when sibling
 * AppShell state (notesOpen, commandBarOpen, focusTask…) changes.
 * Parent passes `useCallback`-stable handlers + a derived `title`
 * string that only changes on route navigation. Default shallow
 * comparator suffices.
 */
export default memo(Topbar);
